import type { ReferenceUser } from "@reactive-resume/dto";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { useEffect, useMemo } from "react";

import { axios } from "@/client/libs/axios";
import { useUser } from "@/client/services/user";

import { type ReferralDashboardData, type ReferralListItem } from "../types/referral";

type ReferencesResponse = {
  page: number;
  data: ReferenceUser[];
};

type ReferralLinkResponse = {
  state: "available" | "blocked" | "purchase-required" | "not-created" | "session-closed";
  link: string | null;
  sessionId: string | null;
  activeReferralCount: number;
  policy: {
    unpaidThreshold: number;
    rewardPerActiveReferralFcfa: number;
    smartProMilestone: number;
  };
};

const fetchMyReferences = async (status: string, page: number) => {
  const params: Record<string, string | number> = { page };
  if (status !== "all") {
    // In backend: closed = active (purchased), opened = inactive (not purchased)
    params.status = status === "active" ? "closed" : "opened";
  }
  const response = await axios.get<ReferencesResponse, AxiosResponse<ReferencesResponse>>(
    "/references/me",
    { params },
  );

  return response.data;
};

const fetchReferralLink = async () => {
  const response = await axios.get<ReferralLinkResponse, AxiosResponse<ReferralLinkResponse>>(
    "/engagement/referral-link",
  );

  return response.data;
};

const createReferralLink = async () => {
  const response = await axios.post<ReferralLinkResponse, AxiosResponse<ReferralLinkResponse>>(
    "/engagement/referral-link",
  );

  return response.data;
};

const getInitials = (name: string) => {
  const initials = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();

  return initials || "CV";
};

const normalizeReferralLink = (link?: string) => {
  if (!link) {
    return null;
  }

  try {
    const backendLink = new URL(link);

    return `${window.location.origin}${backendLink.pathname}${backendLink.search}`;
  } catch {
    return link;
  }
};

const convertReference = (reference: ReferenceUser): ReferralListItem => {
  const registeredAt = reference.downUser.createdAt;

  return {
    id: reference.id,
    name: reference.downUser.name || "Utilisateur Mon CV Pro",
    email: reference.downUser.email,
    initials: getInitials(reference.downUser.name),
    registeredAt: String(registeredAt),
    /*
     * L’API des références ne renvoie pas encore
     * l’offre souscrite par le filleul.
     */
    offerName: null,
    /*
     * Une référence est fermée lorsqu’elle a été
     * validée par une souscription éligible.
     */
    status: reference.status === "closed" ? "active" : "inactive",
  };
};

export const useReferralDashboard = (page: number = 1, statusFilter: string = "all") => {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const {
    data: referencesResponse,
    isPending: referencesLoading,
    error: referencesError,
  } = useQuery({
    queryKey: ["user-space", "referrals", statusFilter, page, user?.id],
    queryFn: () => fetchMyReferences(statusFilter, page),
    enabled: Boolean(user?.id),
  });

  const {
    data: referralLinkData,
    isPending: linkLoading,
    error: linkError,
    refetch: refetchLink,
  } = useQuery({
    queryKey: ["user-space", "referral-link", user?.id],
    queryFn: fetchReferralLink,
    enabled: Boolean(user?.id),
    retry: 1,
  });

  // Auto-create referral session if user is eligible but no link exists.
  // This handles users who paid before the auto-creation was implemented.
  const createMutation = useMutation({
    mutationFn: createReferralLink,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["user-space", "referral-link", user?.id],
      });
    },
  });

  useEffect(() => {
    // If the state is "not-created" (user has paid but no session), create the session automatically
    if (referralLinkData && referralLinkData.state === "not-created" && !createMutation.isPending) {
      createMutation.mutate();
    }
  }, [referralLinkData, createMutation]);

  const isEligible =
    referralLinkData?.state !== "purchase-required" &&
    referralLinkData?.state !== undefined &&
    referralLinkData.link !== null;

  const data = useMemo<ReferralDashboardData>(() => {
    const referrals = referencesResponse?.data.map(convertReference) ?? [];

    const activeReferrals = referralLinkData?.activeReferralCount ?? 0;
    // Compute inactiveReferrals from the current page data.
    // This is the best estimate available without a dedicated total-count endpoint.
    const inactiveOnThisPage = referrals.filter((r) => r.status === "inactive").length;
    const totalReferrals = referencesResponse ? activeReferrals + inactiveOnThisPage : 0;
    const inactiveReferrals = totalReferrals - activeReferrals;

    const referralLink = referralLinkData?.link
      ? normalizeReferralLink(referralLinkData.link)
      : null;

    const policy = referralLinkData?.policy ?? {
      unpaidThreshold: 10,
      rewardPerActiveReferralFcfa: 250,
      smartProMilestone: 15,
    };

    return {
      isEligible: referralLinkData?.state === "available",
      referralLink,
      referrals,
      totalReferrals,
      activeReferrals,
      inactiveReferrals,
      rewardAmount:
        Math.max(0, activeReferrals - policy.unpaidThreshold) * policy.rewardPerActiveReferralFcfa,
      policy,
      totalPages: referencesResponse?.page ?? 1,
    };
  }, [referralLinkData, referencesResponse]);

  return {
    data,
    linkState: referralLinkData?.state ?? null,
    loading: referencesLoading || linkLoading,
    error: referencesError || linkError,
    retryCreateLink: () => createMutation.mutate(),
  };
};
